import { getApiUserManagementV1ApprovalRequests } from '@/libs/api-user-mgt/generated/approvals/approvals';
import { getApiUserManagementV1AuditLog } from '@/libs/api-user-mgt/generated/audit-log/audit-log';
import { getApiUserManagementV1Users } from '@/libs/api-user-mgt/generated/users/users';

import { USER_MANAGEMENT_DEFAULT_PAGE_SIZE } from '@/userManagement/domain/constants/pagination';
import type { PaginatedResponse, UserListParams } from '@/userManagement/domain/types/QueryContracts';

import { parseSieveListPayload } from './pagedWire';
import { toApprovalRequestsQueryParams, toUserManagementAuditQueryParams, toUserManagementUsersQueryParams } from './sieveQuery';
import { normalizeUserListWireRows } from './userListWire';

export type UmPaginatedResource = 'users' | 'auditLog' | 'approvalRequests';

export async function fetchUserManagementPaged<T>(
  resource: UmPaginatedResource,
  params: Record<string, unknown>
): Promise<PaginatedResponse<T>> {
  const page = (params.page as number) ?? 1;
  const pageSize = (params.pageSize as number) ?? USER_MANAGEMENT_DEFAULT_PAGE_SIZE;

  if (resource === 'users') {
    const q = toUserManagementUsersQueryParams(params as UserListParams);
    const r = await getApiUserManagementV1Users(q);
    const { rows, totalCount, serverTotalPages } = parseSieveListPayload<unknown>(r.data, r.headers, pageSize);
    const normalized = normalizeUserListWireRows(rows);
    const adjustedTotal = Math.max(totalCount, normalized.length);
    const totalPages =
      serverTotalPages !== undefined && serverTotalPages > 0 ? serverTotalPages : Math.max(1, Math.ceil(adjustedTotal / pageSize));
    return {
      data: normalized as T[],
      page,
      pageSize,
      totalCount: adjustedTotal,
      totalPages
    };
  }

  if (resource === 'auditLog') {
    const q = toUserManagementAuditQueryParams(params as import('@/userManagement/domain/types/QueryContracts').AuditListParams);
    const r = await getApiUserManagementV1AuditLog(q);
    const { rows, totalCount } = parseSieveListPayload<T>(r.data, r.headers, pageSize);
    return {
      data: rows,
      page,
      pageSize,
      totalCount,
      totalPages: Math.max(1, Math.ceil(totalCount / pageSize))
    };
  }

  if (resource === 'approvalRequests') {
    const q = toApprovalRequestsQueryParams(params as import('@/userManagement/domain/types/QueryContracts').SieveParams);
    const r = await getApiUserManagementV1ApprovalRequests(q);
    const { rows, totalCount } = parseSieveListPayload<T>(r.data, r.headers, pageSize);
    return {
      data: rows,
      page,
      pageSize,
      totalCount,
      totalPages: Math.max(1, Math.ceil(totalCount / pageSize))
    };
  }

  throw new Error(`fetchUserManagementPaged: unknown resource ${resource satisfies never}`);
}

/**
 * @deprecated Prefer `fetchUserManagementPaged` — kept for call sites that pass a URL string.
 */
export const UM_PAGINATED_PATHS = {
  users: 'users',
  auditLog: 'auditLog',
  approvalRequests: 'approvalRequests'
} as const;

export function paginatedGet<T>(url: string, params: Record<string, unknown>): Promise<PaginatedResponse<T>> {
  if (url.includes('umUsers') || url.endsWith('users') || url === '/api/umUsers') {
    return fetchUserManagementPaged<T>('users', params);
  }
  if (url.includes('umAuditLog') || url.includes('audit')) {
    return fetchUserManagementPaged<T>('auditLog', params);
  }
  if (url.includes('umApproval') || url.includes('approval')) {
    return fetchUserManagementPaged<T>('approvalRequests', params);
  }
  return fetchUserManagementPaged<T>('users', params);
}

export function mapUserListParamsForServer(params: UserListParams): Record<string, unknown> {
  const {
    roleIds,
    roleNotAssigned,
    databases,
    hasRoleDeviation,
    changesOnly,
    permissionSnapshotId,
    snapshotChangesOnly,
    omitActivityFilter: _omitActivityFilter,
    ...rest
  } = params;
  void _omitActivityFilter;
  const out: Record<string, unknown> = { ...rest };
  if (roleIds !== undefined && roleIds.length > 0) {
    out.roleIds = roleIds;
  }
  if (roleNotAssigned === true) {
    out.roleNotAssigned = true;
  }
  if (databases !== undefined && databases.length > 0) {
    out.databases = databases;
  }
  if (hasRoleDeviation === true) {
    out.hasRoleDeviation = true;
  }
  if (changesOnly === true) {
    out.changesOnly = true;
  }
  if (typeof permissionSnapshotId === 'number' && Number.isInteger(permissionSnapshotId) && permissionSnapshotId > 0) {
    out.permissionSnapshotId = permissionSnapshotId;
  }
  if (snapshotChangesOnly === true) {
    out.snapshotChangesOnly = true;
  }
  return out;
}
